import React, { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { Box, Button, Chip, CircularProgress, Stack, SxProps, Theme, Typography } from "@mui/material";
import { useFeedConfigSettings } from "components/News-feed/hooks/use-feed-config-settings";
import { useDidUpdate } from "hooks";
import { useNewsFeedActions, useUserActions } from "store";
import { IFeedItem } from "store/types";
import type { ArticleRow } from "store/types/news";

import { InvestmentsNews } from "./Rows/InvestmentsNews";
import { PlatformUpdates } from "./Rows/PlatformUpdates";

// ─── Types ───────────────────────────────────────────────────────────────────

type FilterKey = "platform" | "investment";

interface FilterOption {
  key: FilterKey;
  label: string;
  apiType: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

// API accepts: "all" | "notifications" | "news"
// (matches newsFilterOptions in constants/dashboard-news.ts)
const FILTERS: FilterOption[] = [
  { key: "investment", label: "Investment News", apiType: "news" },
  { key: "platform", label: "Platform Updates", apiType: "notifications" }
];

const defaultPage = 1;

interface Props {
  sx?: SxProps<Theme>;
}

const ARTICLES_PER_PAGE = 20;

export const Feed: React.FC<Props> = ({ sx }) => {
  const { getFeedAction } = useUserActions();

  const { getArticlesAction, UpdateNewsServiceAction } = useNewsFeedActions();
  const { openConfigureFeed } = useFeedConfigSettings();

  const [activeFilter, setActiveFilter] = useState<FilterKey>("investment");
  const [data, setData] = useState<IFeedItem[]>([]);
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(defaultPage);

  const currentFilter = FILTERS.find((f) => f.key === activeFilter)!;
  const isInvestmentNews = activeFilter === "investment";

  const fetchPage = useCallback(
    async (nextPage: number) => {
      try {
        setLoading(true);
        if (isInvestmentNews) {
          const res = await getArticlesAction({ page: nextPage, limit: ARTICLES_PER_PAGE });
          setArticles((prev) => (nextPage === 1 ? res.articles : [...prev, ...res.articles]));
          setHasMore(res.has_more);
          setPage(res.page + 1);
        } else {
          const { data: feeds, last_page } = await getFeedAction({
            type: currentFilter.apiType,
            search: "",
            page: nextPage
          });
          setData((prev) => (nextPage === 1 ? feeds : [...prev, ...feeds]));
          setHasMore(nextPage < last_page);
          setPage(nextPage + 1);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [isInvestmentNews, getArticlesAction, getFeedAction, currentFilter.apiType]
  );

  useEffect(() => {
    fetchPage(defaultPage);
  }, []);

  useDidUpdate(() => {
    setData([]);
    setArticles([]);
    setPage(1);
    setHasMore(true);
    setLoading(false);
    fetchPage(1);
  }, [activeFilter]);

  const rows = useMemo(() => {
    if (isInvestmentNews) {
      return articles.map((article) => <InvestmentsNews key={article.id} data={article} />);
    }
    return data.map((item) => <PlatformUpdates key={item.id} data={item} />);
  }, [isInvestmentNews, articles, data]);

  const dataLength = isInvestmentNews ? articles.length : data.length;

  useEffect(() => {
    UpdateNewsServiceAction();
  }, []);
  return (
    <Stack
      sx={{
        height: "670px",
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
        ...sx
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: "16px", pt: "16px", pb: "12px", flexShrink: 0 }}
      >
        <Typography sx={{ fontWeight: 600, color: "#071F35" }} variant="h6">
          Feed
        </Typography>
        <Stack direction="row" alignItems="center" gap="6px">
          <Button
            variant="outlined"
            size="sm"
            startIcon={<TuneOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => openConfigureFeed()}
            sx={{
              fontSize: "12px",
              lineHeight: "16px",
              color: "#252D39",
              // px: "10px",
              textTransform: "none"
              // "&:hover": { backgroundColor: "#F0F4FA", borderColor: "#C5CDD8" }
            }}
          >
            Feed settings
          </Button>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        gap="6px"
        sx={{ px: "16px", pb: "12px", flexShrink: 0, flexWrap: "nowrap", overflowX: "auto" }}
      >
        {FILTERS.map((filter) => (
          <Chip
            key={filter.key}
            label={filter.label}
            size="small"
            onClick={() => setActiveFilter(filter.key)}
            sx={{
              fontSize: "12px",
              fontWeight: activeFilter === filter.key ? 600 : 400,
              backgroundColor: activeFilter === filter.key ? "#DBE8F5" : "#ECF1F8",
              color: "#071F35",
              border: "none",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#DBE8F5"
              }
            }}
          />
        ))}
      </Stack>

      <Box sx={{ flex: 1, overflow: "hidden", px: "16px", pt: "12px", display: "flex", flexDirection: "column" }}>
        <InfiniteScroll
          dataLength={dataLength}
          next={() => fetchPage(page)}
          hasMore={hasMore}
          height="100%"
          loader={
            loading && dataLength > 0 ? (
              <Stack alignItems="center" py="16px">
                <CircularProgress size={24} />
              </Stack>
            ) : null
          }
          endMessage={
            dataLength > 0 ? (
              <Typography sx={{ textAlign: "center", py: "12px", fontSize: "12px", color: "#97A6BF" }}>
                You&apos;re all caught up
              </Typography>
            ) : null
          }
          style={{ paddingBottom: "12px" }}
        >
          {loading && dataLength === 0 ? (
            <Stack alignItems="center" py="32px">
              <CircularProgress size={24} />
            </Stack>
          ) : rows.length === 0 && !loading ? (
            <Typography sx={{ textAlign: "center", py: "32px", fontSize: "13px", color: "#97A6BF" }}>
              No items to show
            </Typography>
          ) : (
            rows
          )}
        </InfiniteScroll>
      </Box>
    </Stack>
  );
};
